import { Button } from '@/components/ui/button';
import type { CardEnvelope } from '@borradh-workspace/contracts/claire';
import { useState } from 'react';

import {
  useComposerGate,
  useCurrentComposerTurn,
  useDecisionActionsDisabled,
} from '../composer-gate-context';

type ConfirmationExpired = Extract<
  CardEnvelope,
  { type: 'confirmation_expired' }
>;

/**
 * An approval the server refused to run.
 *
 * This used to render nothing: the owner pressed Confirm, Claire said the
 * approval "didn't go through", and there was no card left to press. It now
 * says nothing was changed and, when no other decision is waiting, offers
 * one button that asks Claire to raise the card again.
 *
 * `no_user_turn` stays silent: it means Claire tried to run a card in the same
 * turn that raised it, and that card is on screen. `consumed` and `declined`
 * get no button: the change already ran, or the owner said no.
 */
export function ApprovalNotRunNotice({
  presentation,
  onAskAgain,
}: {
  presentation: ConfirmationExpired;
  onAskAgain: (text: string) => unknown;
}) {
  const currentTurn = useCurrentComposerTurn();
  const { activeGate } = useComposerGate();
  const actionsDisabled = useDecisionActionsDisabled();
  const [asked, setAsked] = useState(false);

  const { reason } = presentation;
  if (reason === 'no_user_turn') return null;

  const message =
    reason === 'consumed'
      ? 'This approval was already used.'
      : reason === 'declined'
        ? 'You cancelled this, so nothing was changed.'
        : 'This approval didn’t go through, so nothing was changed.';
  // Another card waiting in the composer is already something to press.
  const canAskAgain =
    reason !== 'consumed' &&
    reason !== 'declined' &&
    currentTurn &&
    !activeGate &&
    !asked;

  return (
    <div
      role="status"
      data-testid="claire-card-approval-not-run"
      className="flex w-full flex-wrap items-center justify-between gap-2 rounded-lg border bg-card px-4 py-3 text-sm sm:max-w-lg"
    >
      <p className="text-muted-foreground">{message}</p>
      {canAskAgain ? (
        <Button
          size="sm"
          variant="outline"
          disabled={actionsDisabled}
          onClick={() => {
            setAsked(true);
            onAskAgain('Please show me that approval again.');
          }}
        >
          Ask again
        </Button>
      ) : null}
    </div>
  );
}
